import { z } from 'zod';
import { type CatalogEntry, defineCatalogEntry, OwnedIdSchema } from './entry';

/**
 * Watchlist panels of the board catalog — owned by the Watchlist area. Add watchlist entries
 * HERE, never in another area's file; a duplicate id across area files is a compile error
 * (index.ts).
 *
 * Every entry must fetch its own data from simple PROPS (ids, enums) through its feature's
 * api-query hooks — a component that takes DATA props fails that criterion.
 *
 * THE ID IS THE USER'S OWN, and the composer may only use one the main agent put in the brief
 * (ui-composition skill). Ownership is the SERVER's: a foreign or stale id is a 404, which the
 * FE wrapper renders as a calm "not available" panel (`WatchlistPanelGate`), never a crash.
 *
 * ZOD-3 COMPATIBILITY: apps/web bundles this SOURCE with zod v3 while the backend runs v4 —
 * only syntax valid in BOTH may be used here.
 *
 * Lifecycle rules (§4.6) — an id is NEVER repurposed (persisted ops reference it forever);
 * a semantic change is a NEW entry; `retired: true` drops an entry from the composer
 * prompt while its FE binding stays so historical boards keep rendering. Props changes are
 * additive (new fields optional/defaulted); a breaking change = new id + retire the old.
 */
export const WATCHLIST_PANELS = {
  'watchlist-holdings': defineCatalogEntry({
    id: 'watchlist-holdings',
    category: 'watchlist',
    description:
      'The symbols on one of the user\'s watchlists with price, day change and key figures. Use for "how is my watchlist doing" or to review watched names side by side.',
    props: z.object({ watchlistId: OwnedIdSchema }),
    adjustable: [],
    breadcrumb: ['Watchlist', 'Holdings'],
    example: { watchlistId: '9b1d4f3a-2c6e-4d8b-a7f0-3e5c1b2d4a69' },
  }),
} as const satisfies Record<string, CatalogEntry>;
